import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Home, Shield, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalPage = () => {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPage();
  }, [pageType]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/legal/${pageType}`);
      setPage(response.data);
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <Shield size={48} className="mx-auto mb-4 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Page non trouvée</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 rounded-lg text-white"
            style={{ background: '#335C2E' }}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80"
              style={{ color: '#335C2E' }}
            >
              <ArrowLeft size={20} />
              Retour à l'accueil
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100"
              style={{ color: '#335C2E' }}
            >
              <Home size={18} />
              Accueil
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="mb-8 pb-6 border-b">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={32} style={{ color: '#335C2E' }} />
              <h1 className="text-3xl font-bold" style={{ color: '#335C2E' }}>
                {page.title}
              </h1>
            </div>
            {page.last_updated && (
              <p className="text-sm flex items-center gap-2" style={{ color: '#666666' }}>
                <Calendar size={16} />
                Dernière mise à jour : {new Date(page.last_updated).toLocaleDateString('fr-CA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none legal-content"
            dangerouslySetInnerHTML={{ __html: page.content }}
            style={{ color: '#333333' }}
          />

          {/* Footer info */}
          <div className="mt-12 pt-6 border-t">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <p className="text-sm" style={{ color: '#666666' }}>
                Refacing VR - Division Pavillons
              </p>
              <div className="flex gap-4">
                {pageType === 'privacy_policy' ? (
                  <button
                    onClick={() => navigate('/legal/terms_of_service')}
                    className="text-sm hover:underline"
                    style={{ color: '#335C2E' }}
                  >
                    Conditions d'utilisation →
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/legal/privacy_policy')}
                    className="text-sm hover:underline"
                    style={{ color: '#335C2E' }}
                  >
                    Politique de confidentialité →
                  </button>
                )}
                <button
                  onClick={() => navigate('/data-deletion')}
                  className="text-sm hover:underline"
                  style={{ color: '#335C2E' }}
                >
                  Supprimer mes données →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12" style={{ background: '#2A4A24', color: 'white' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm opacity-90">
            © 2025 Refacing VR. Tous droits réservés.
          </p>
        </div>
      </footer>

      <style>{`
        .legal-content h2 {
          color: #335C2E;
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .legal-content h3 {
          color: #333333;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .legal-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        .legal-content ul, .legal-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .legal-content li {
          margin-bottom: 0.5rem;
        }
        .legal-content a {
          color: #335C2E;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default LegalPage;
